import { useMemo } from "react";
import { useGetList } from "ra-core";
import { Link, useParams } from "react-router";
import { ChevronRight, ClipboardCheck, Layers } from "lucide-react";

import type { Contact } from "../types";
import type { AgentWriteProposal } from "./proposalReview";
import { ProposalsPage } from "./ProposalsPage";
import { ClassificationReviewPage } from "./ClassificationReviewPage";
import { SwipeRunner } from "./SwipeRunner";
import { SWIPE_SCENARIOS, getScenario } from "./scenarios";
import { resolveFilter, type SwipeScenario } from "./types";

/**
 * Rapid triage over the CRM, one question at a time.
 *
 * The premise: the decisions that make the relationship data worth querying —
 * how well you know someone, whether they could buy, whether they open a door,
 * whether a quiet relationship is worth restarting — are each a second's
 * judgement and an hour's chore in a list view. So each is a scenario: a
 * filter that decides who gets asked, and two to four actions bound to
 * gestures. The interface is shared; only those two things differ.
 *
 * Everything an action writes is a normal contact field, so the Kanban board,
 * the filters and the MCP tools see the results immediately; nothing here is a
 * private scoring system.
 */
export const SwipePage = () => {
  const { scenarioId } = useParams();
  const scenario = getScenario(scenarioId);

  if (scenarioId && !scenario) {
    return (
      <div className="mx-auto max-w-md p-4">
        <p className="text-sm text-muted-foreground">
          No scenario called “{scenarioId}”.
        </p>
        <Link to="/swipe" className="text-sm underline">
          Back to the scenarios
        </Link>
      </div>
    );
  }

  return (
    <div className="p-2 sm:p-4">
      {scenario ? <SwipeRunner scenario={scenario} /> : <ScenarioPicker />}
    </div>
  );
};

SwipePage.path = "/swipe";
SwipePage.scenarioPath = "/swipe/:scenarioId";

const ScenarioPicker = () => (
  <div className="mx-auto flex w-full max-w-md flex-col gap-4">
    <div>
      <h1 className="flex items-center gap-2 text-2xl font-semibold">
        <Layers className="h-6 w-6" aria-hidden />
        Triage
      </h1>
      <p className="mt-1 text-sm text-muted-foreground">
        One question, one gesture. Every answer writes a real contact field.
      </p>
    </div>
    <div className="flex flex-col gap-2">
      <ProposalsRow />
      <Link to={ClassificationReviewPage.path} className="flex items-center gap-3 rounded-xl border bg-card p-4 transition-colors hover:bg-accent">
        <ClipboardCheck className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden />
        <div className="min-w-0 flex-1"><p className="font-semibold">Pending classifications</p>
          <p className="text-sm text-muted-foreground">Review unresolved fields and give workflow feedback.</p></div>
        <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
      </Link>
      {SWIPE_SCENARIOS.map((scenario) => (
        <ScenarioRow key={scenario.id} scenario={scenario} />
      ))}
    </div>
  </div>
);

/**
 * Entry point into the proposal review deck (#24), shown here rather than
 * added to the mobile bottom nav — that bar is already six items wide, and
 * AGENTS.md's own guidance is not to over-invest in chrome. Triage and
 * proposal review are the same shape of task (one question, one gesture, one
 * queue), so this is where a reviewer already looking for something to
 * triage would look for it.
 */
const ProposalsRow = () => {
  const isDemo = import.meta.env.VITE_IS_DEMO === "true";
  const { total, isPending } = useGetList<AgentWriteProposal>(
    "agent_write_proposals",
    {
      pagination: { page: 1, perPage: 1 },
      sort: { field: "created_at", order: "ASC" },
      filter: { status: "pending" },
    },
    { enabled: !isDemo },
  );

  return (
    <Link
      to={ProposalsPage.path}
      className="flex items-center gap-3 rounded-xl border bg-card p-4 transition-colors hover:bg-accent"
    >
      <ClipboardCheck
        className="h-5 w-5 shrink-0 text-muted-foreground"
        aria-hidden
      />
      <div className="min-w-0 flex-1">
        <p className="font-semibold">Proposals</p>
        <p className="text-sm text-muted-foreground">
          Writes an unattended agent could not confirm itself.
        </p>
      </div>
      {!isDemo && (
        <div className="shrink-0 text-right">
          <span className="block text-lg font-semibold tabular-nums">
            {isPending ? "—" : (total ?? 0).toLocaleString()}
          </span>
          <span className="text-[11px] text-muted-foreground">waiting</span>
        </div>
      )}
      <ChevronRight
        className="h-4 w-4 shrink-0 text-muted-foreground"
        aria-hidden
      />
    </Link>
  );
};

const ScenarioRow = ({ scenario }: { scenario: SwipeScenario }) => {
  // Counted against the scenario's own filter, unrefined: the number here is
  // the size of the job, and the filter bar inside narrows it from there.
  const now = useMemo(() => new Date(), []);
  const { total, isPending } = useGetList<Contact>("contacts", {
    pagination: { page: 1, perPage: 1 },
    sort: scenario.sort,
    filter: resolveFilter(scenario.filter, now),
  });

  return (
    <Link
      to={`/swipe/${scenario.id}`}
      className="flex items-center gap-3 rounded-xl border bg-card p-4 transition-colors hover:bg-accent"
    >
      <div className="min-w-0 flex-1">
        <p className="font-semibold">{scenario.title}</p>
        <p className="text-sm text-muted-foreground">{scenario.question}</p>
        <p className="mt-1 text-xs text-muted-foreground">
          {scenario.rationale}
        </p>
      </div>
      <div className="shrink-0 text-right">
        <span className="block text-lg font-semibold tabular-nums">
          {isPending ? "—" : (total ?? 0).toLocaleString()}
        </span>
        <span className="text-[11px] text-muted-foreground">waiting</span>
      </div>
      <ChevronRight
        className="h-4 w-4 shrink-0 text-muted-foreground"
        aria-hidden
      />
    </Link>
  );
};
